import type { SortMethodType } from '@sdk/schedule/types/SortMethodType';

import type {
  GroupsArray,
  UnassignedGroup,
  AvailableGroup,
  AllGroup,
  PositionGroup,
  LocationGroup,
  TagGroup,
  GroupGroup,
  EventsByDate,
  NoGroupsGroup,
  NoTagsGroup,
  DayPartGroup,
  NoDayPartGroup,
} from '../../types/GroupsArrayType';
import { UNASSIGNED, AVAILABLE } from '../constants';

import shallowCopyEvents from './shallowCopyEvents';

type ExtractedGroups = {
  unassigned: UnassignedGroup;
  available: AvailableGroup;
  all: AllGroup | GroupsArray;
  unassignedEvents: EventsByDate;
  availableEvents: EventsByDate;
};

export default function extractFromGroupsArray(
  groupsArray: GroupsArray,
  groupBy: SortMethodType
): ExtractedGroups {
  const unassigned = groupsArray.find((g) => g.type === UNASSIGNED) as UnassignedGroup;
  const available = groupsArray.find((g) => g.type === AVAILABLE) as AvailableGroup;
  const all = getAll(groupsArray, groupBy);
  const unassignedEvents = unassigned ? shallowCopyEvents(unassigned.users[0].events) : {};
  const availableEvents = available ? shallowCopyEvents(available.users[0].events) : {};

  return {
    unassigned,
    available,
    all,
    unassignedEvents,
    availableEvents,
  };
}

function getAll(groupsArray: GroupsArray, groupBy: SortMethodType): AllGroup | GroupsArray {
  if (groupBy === 'position') {
    return groupsArray.filter((g) => g.type === 'position') as Array<PositionGroup>;
  }
  if (groupBy === 'location') {
    return groupsArray.filter((g) => g.type === 'location') as Array<LocationGroup>;
  }
  if (groupBy === 'tag') {
    const tags = groupsArray.filter((g) => g.type === 'tag') as Array<TagGroup>;
    const noTag = groupsArray.find((g) => g.type === 'noTags') as NoTagsGroup;

    const result: Array<TagGroup | NoTagsGroup> = [...tags];
    if (noTag) {
      result.push(noTag);
    }
    return result;
  }
  if (groupBy === 'group') {
    const groups = groupsArray.filter((g) => g.type === 'group') as Array<GroupGroup>;
    const noGroup = groupsArray.find((g) => g.type === 'noGroups') as NoGroupsGroup;

    const result: Array<GroupGroup | NoGroupsGroup> = [...groups];
    if (noGroup) {
      result.push(noGroup);
    }
    return result;
  }
  if (groupBy === 'dayPart') {
    const dayParts = groupsArray.filter((g) => g.type === 'dayPart') as Array<DayPartGroup>;
    const noDayPart = groupsArray.find((g) => g.type === 'otherDayPart') as NoDayPartGroup;

    const result: Array<DayPartGroup | NoDayPartGroup> = [...dayParts];
    if (noDayPart) {
      result.push(noDayPart);
    }
    return result;
  }
  return groupsArray.find((g) => g.type === 'all') as AllGroup;
}
